import { useState, useMemo, useEffect } from "react";
import {
  FormControl,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Button,
  IconButton,
  TextField,
  Tooltip,
} from "@mui/material";
import { FaFileDownload } from "react-icons/fa";
import { MdVisibility } from "react-icons/md";
import { Autocomplete } from "@mui/material";
import FileImportPrePicking from "components/FileImport/FileImportPrePicking";
import PrePickingReport from "components/PrePicking/PrePickingReport";
import useCrud from "hooks/useCrud";
import { useToast } from "hooks/useToast";
import { axiosGet } from "api";
import {
  ArticleLocationSummary,
  ArticleLocationSummaryExportResponse,
  Client,
  PrePickingValidationResponse,
  PrePickingValidationShortageResponse,
  StockExportGroupBy,
  StockStatusFilter,
} from "types";
import CustomGrid from "components/lists/CustomGrid";
import {
  ARTICLE_LOCATIONS_SUMMARY_EXPORT_URL,
  ARTICLE_LOCATIONS_URL,
} from "constants/urls";
import { useSession } from "providers/SessionProvider";
import { queryBuilder } from "helpers/query";
import { buildExcelHeader } from "helpers/exportToExcel";
import { getScopedClientIds } from "helpers/clientScope";
import { GridColDef } from "@mui/x-data-grid";
import { Roles, STATUS_CODES } from "constants/index";
import { getStockStatusOption, stockStatusOptions } from "helpers/stockStatus";
import Loader from "components/lists/Loader";
import StockExportGroupingModal from "./StockExportGroupingModal";
import ObservationRecordsModal, {
  ObservationRecordItem,
} from "components/utils/ObservationRecordsModal";
import {
  exportGroupedStockToExcel,
  exportGroupedStockToPdf,
  exportStockToExcel,
  exportStockToPdf,
  StockExportColumn,
} from "helpers/stockExports";

const getAmountPerContainerSummaryLabel = (
  articleLocationSummary: ArticleLocationSummary,
) =>
  articleLocationSummary.amount_per_container_label?.trim() ||
  articleLocationSummary.amount_per_container?.toString() ||
  "-";

export default function StockSummaryList() {
  const { clients, articles, groups, userData } = useSession();
  const { showSnackBar } = useToast();
  const isClientUser = userData?.role === Roles.CLIENT;

  const {
    data: stockSummary,
    fetchData: getStockSummary,
    loading: stockSummaryLoading,
  } = useCrud<ArticleLocationSummary>(`${ARTICLE_LOCATIONS_URL}/summary`);

  const [selectionModel, setSelectionModel] = useState<any[]>([]);
  const [filters, setFilters] = useState({
    clientIds: [] as number[],
    lineIds: [] as number[],
    articleCode: "",
    articleDescription: "",
    lote: "",
    oc: "",
    amount_per_container: "",
    fecha_vencimiento: "",
    fecha_ingreso: "",
    groupId: undefined as number | undefined,
    status: "all" as StockStatusFilter,
    movementId: "",
  });
  const [selectedClients, setSelectedClients] = useState<Client[]>([]);
  const [selectedLineClients, setSelectedLineClients] = useState<Client[]>([]);
  const [prePickingClientId, setPrePickingClientId] = useState<number | null>(
    userData?.role === Roles.CLIENT ? userData.client?.id ?? null : null,
  );
  const [prePickingValidationResult, setPrePickingValidationResult] =
    useState<PrePickingValidationResponse | null>(null);
  const [movementIdInput, setMovementIdInput] = useState("");
  const [articleDescriptionInput, setArticleDescriptionInput] = useState("");
  const [exportLoading, setExportLoading] = useState(false);
  const [exportModalOpen, setExportModalOpen] = useState(false);
  const [pendingExportFormat, setPendingExportFormat] = useState<
    "excel" | "pdf" | null
  >(null);
  const [selectedObservationRow, setSelectedObservationRow] =
    useState<ArticleLocationSummary | null>(null);

  const selectedObservationRecords: ObservationRecordItem[] =
    selectedObservationRow
      ? [
          {
            id: [
              "summary",
              selectedObservationRow.article_code,
              selectedObservationRow.lote ?? "sin-lote",
              selectedObservationRow.oc ?? "sin-oc",
              selectedObservationRow.status ?? "available",
            ].join("-"),
            title: "Observación consolidada",
            quantity: Number(selectedObservationRow.stock_total ?? 0),
            status: selectedObservationRow.status ?? "available",
            observation: selectedObservationRow.observation ?? null,
            history: [],
          },
        ]
      : [];

  const activeClients = useMemo(
    () => clients.filter((client) => client.is_active && client.parent_client_id == null),
    [clients],
  );

  const prePickingSelectedClient = useMemo(
    () =>
      activeClients.find((client) => client.id === prePickingClientId) ?? null,
    [activeClients, prePickingClientId],
  );

  const prePickingScopedClientIds = useMemo(
    () =>
      prePickingClientId ? getScopedClientIds(clients, prePickingClientId) : [],
    [clients, prePickingClientId],
  );

  const prePickingArticles = useMemo(
    () =>
      articles.filter((article) =>
        prePickingScopedClientIds.includes(article.client_id),
      ),
    [articles, prePickingScopedClientIds],
  );

  const uniqueLotes = useMemo(
    () => Array.from(new Set(stockSummary.map((a) => a.lote).filter(Boolean))),
    [stockSummary],
  );
  const uniqueOCs = useMemo(
    () => Array.from(new Set(stockSummary.map((a) => a.oc).filter(Boolean))),
    [stockSummary],
  );
  const uniqueAmountPerContainer = useMemo(
    () =>
      Array.from(
        new Set(
          stockSummary.flatMap((articleLocationSummary) =>
            (
              articleLocationSummary.amount_per_container_values?.length
                ? articleLocationSummary.amount_per_container_values
                : [articleLocationSummary.amount_per_container]
            )
              .map((value) => String(value))
              .filter((value) => value.trim() !== ""),
          ),
        ),
      ),
    [stockSummary],
  );
  const uniqueVencimientos = useMemo(
    () =>
      Array.from(
        new Set(stockSummary.map((a) => a.fecha_vencimiento).filter(Boolean)),
      ),
    [stockSummary],
  );
  const uniqueIngresos = useMemo(
    () =>
      Array.from(
        new Set(stockSummary.map((a) => a.fecha_ingreso).filter(Boolean)),
      ),
    [stockSummary],
  );
  const lineOptions = useMemo(
    () => {
      if (isClientUser) {
        const assignedClient = clients.find(
          (client) => client.id === userData?.client_id,
        );

        if (!assignedClient || assignedClient.parent_client_id != null) {
          return [];
        }

        return (assignedClient.child_clients ?? []).filter(
          (client) => client.is_active,
        );
      }

      return clients.filter(
        (client) =>
          client.is_active &&
          client.parent_client_id != null &&
          filters.clientIds.includes(client.parent_client_id),
      );
    },
    [clients, filters.clientIds, isClientUser, userData?.client_id],
  );

  const queryClientIds = useMemo(
    () => (filters.lineIds.length > 0 ? filters.lineIds : filters.clientIds),
    [filters.clientIds, filters.lineIds],
  );

  const scopedClientIds = useMemo(
    () => [
      ...queryClientIds,
      ...clients
        .filter((client) =>
          queryClientIds.includes(client.parent_client_id ?? 0),
        )
        .map((client) => client.id),
    ],
    [clients, queryClientIds],
  );

  const visibleArticleCodes = useMemo(
    () => new Set(stockSummary.map((row) => row.article_code)),
    [stockSummary],
  );

  const uniqueArticles = useMemo(() => {
    const scopedArticles =
      queryClientIds.length > 0
        ? articles.filter((article) =>
            scopedClientIds.includes(article.client_id),
          )
        : articles;

    const visibleArticles = isClientUser
      ? scopedArticles.filter((article) =>
          visibleArticleCodes.has(article.code),
        )
      : scopedArticles;

    return Array.from(
      new Map(
        visibleArticles.map((article) => [article.code, article]),
      ).values(),
    );
  }, [
    articles,
    isClientUser,
    queryClientIds,
    scopedClientIds,
    visibleArticleCodes,
  ]);

  const groupOptions = useMemo(
    () =>
      groups.filter(
        (group) =>
          queryClientIds.length === 0 ||
          scopedClientIds.includes(group.client_id || 0),
      ),
    [groups, queryClientIds, scopedClientIds],
  );

  const handlePrePickingValidated = (
    response: PrePickingValidationResponse,
  ) => {
    setPrePickingValidationResult(response);
  };

  const prePickingShortageResult =
    prePickingValidationResult &&
    !prePickingValidationResult.success &&
    "report" in prePickingValidationResult
      ? (prePickingValidationResult as PrePickingValidationShortageResponse)
      : null;

  useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      groupId: undefined,
      articleCode: "",
    }));
  }, [queryClientIds]);

  useEffect(() => {
    if (
      userData?.role === Roles.CLIENT &&
      userData.client?.id &&
      prePickingClientId !== userData.client.id
    ) {
      setPrePickingClientId(userData.client.id);
    }
  }, [prePickingClientId, userData]);

  const buildStockSummaryQuery = () =>
    queryBuilder({
      clientIds: queryClientIds,
      articleCode: filters.articleCode || undefined,
      articleDescription: filters.articleDescription.trim() || undefined,
      lote: filters.lote || undefined,
      oc: filters.oc || undefined,
      amount_per_container: filters.amount_per_container || undefined,
      fecha_vencimiento: filters.fecha_vencimiento || undefined,
      fecha_ingreso: filters.fecha_ingreso || undefined,
      groupId: filters.groupId,
      status: filters.status,
      movementId: filters.movementId || undefined,
    });

  const buildStockSummaryExportQuery = (groupBy: StockExportGroupBy) =>
    queryBuilder({
      clientIds: queryClientIds,
      articleCode: filters.articleCode || undefined,
      articleDescription: filters.articleDescription.trim() || undefined,
      lote: filters.lote || undefined,
      oc: filters.oc || undefined,
      amount_per_container: filters.amount_per_container || undefined,
      fecha_vencimiento: filters.fecha_vencimiento || undefined,
      fecha_ingreso: filters.fecha_ingreso || undefined,
      groupId: filters.groupId,
      status: filters.status,
      movementId: filters.movementId || undefined,
      groupBy,
      _exportTimestamp: Date.now(),
    });

  const handleSearch = async () => {
    const query = buildStockSummaryQuery();
    await getStockSummary(query, true);
  };

  useEffect(() => {
    handleSearch();
  }, [
    filters.clientIds,
    filters.lineIds,
    filters.articleCode,
    filters.articleDescription,
    filters.lote,
    filters.oc,
    filters.amount_per_container,
    filters.fecha_vencimiento,
    filters.fecha_ingreso,
    filters.groupId,
    filters.status,
    filters.movementId,
  ]);

  const filterVisibility = useMemo(() => {
    if (selectedClients.length === 0) {
      return {
        show_lote: true,
        show_oc: true,
        show_fecha_vencimiento: true,
      };
    }

    const show_lote = selectedClients.some((c) => c.show_lote);
    const show_oc = selectedClients.some((c) => c.show_oc);
    const show_fecha_vencimiento = selectedClients.some(
      (c) => c.show_fecha_vencimiento,
    );

    return { show_lote, show_oc, show_fecha_vencimiento };
  }, [selectedClients]);

  const structure = useMemo(
    () => ({
      name: "stockSummary",
      labelName: "Stock Total / Resumido",
      fields: [
        { field: "article_code", headerName: "Código", flex: 1, minWidth: 120 },
        { field: "article_description", headerName: "Descripción", flex: 1.5, minWidth: 240 },
        { field: "client_code", headerName: "Cliente", flex: 0.8, minWidth: 140 },
        filterVisibility.show_lote && {
          field: "lote",
          headerName: "Lote",
          minWidth: 140,
          flex: 1,
        },
        filterVisibility.show_oc && { field: "oc", headerName: "OC", flex: 1, minWidth: 120 },
        filterVisibility.show_fecha_vencimiento && {
          field: "fecha_vencimiento",
          headerName: "Fecha Vencimiento",
          minWidth: 180,
          flex: 1,
        },
        { field: "fecha_ingreso", headerName: "Fecha Ingreso", flex: 1, minWidth: 150 },
        {
          field: "status_label",
          headerName: "Estado de mercadería",
          minWidth: 190,
          flex: 1,
          valueGetter: (p: any) => p.row.status_label ?? "-",
        },
        {
          field: "stock_total",
          headerName: "Cant.",
          minWidth: 100,
          flex: 1,
          valueGetter: (p: any) => Number(p.row.stock_total ?? 0),
        },
        {
          field: "amount_per_container",
          headerName: "Cant. x Contenedor",
          minWidth: 180,
          flex: 1,
          valueGetter: (p: any) =>
            getAmountPerContainerSummaryLabel(p.row as ArticleLocationSummary),
        },
        {
          field: "total",
          headerName: "Saldo",
          minWidth: 120,
          flex: 1,
          valueGetter: (p: any) =>
            Number(p.row.saldo ?? p.row.total ?? 0).toFixed(2),
        },
        { field: "unit_description", headerName: "Unidad de Medida", flex: 1, minWidth: 170 },
        { field: "presentation", headerName: "Presentación", flex: 1, minWidth: 150 },
        {
          field: "observation",
          headerName: "Observación",
          width: 110,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          align: "center",
          headerAlign: "center",
          renderCell: (params: any) => (
            <Tooltip title="Ver observaciones">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setSelectedObservationRow(params.row);
                }}
              >
                <MdVisibility fontSize="small" />
              </IconButton>
            </Tooltip>
          ),
        },
      ].filter(Boolean) as GridColDef[],
    }),
    [filterVisibility],
  );

  const buildStockSummaryExportColumns = (
    groupBy: StockExportGroupBy,
  ): StockExportColumn<ArticleLocationSummary>[] => {
    const columns: StockExportColumn<ArticleLocationSummary>[] = [];

    if (groupBy === "location") {
      columns.push({
        id: "location",
        header: "Ubicación",
        value: (a) => a.location_code ?? "-",
        pdfCellWidth: 16,
      });
    }

    columns.push(
      {
        id: "article",
        header: "Código",
        value: (a) => a.article_code ?? "-",
        pdfCellWidth: 20,
      },
      {
        id: "description",
        header: "Desc.",
        value: (a) => a.article_description ?? "-",
        pdfMinCellWidth: 28,
      },
      {
        id: "client",
        header: "Cliente",
        value: (a) => a.client_code ?? "-",
        pdfCellWidth: 14,
      },
    );

    if (filterVisibility.show_lote) {
      columns.push({
        id: "lote",
        header: "Lote",
        value: (a) => a.lote ?? "-",
        pdfCellWidth: 18,
      });
    }

    if (filterVisibility.show_oc) {
      columns.push({
        id: "oc",
        header: "OC",
        value: (a) => a.oc ?? "-",
        pdfCellWidth: 18,
      });
    }

    if (filterVisibility.show_fecha_vencimiento) {
      columns.push({
        id: "fecha_vencimiento",
        header: "F. Venc.",
        value: (a) => a.fecha_vencimiento ?? "-",
        pdfCellWidth: 16,
      });
    }

    columns.push(
      {
        id: "fecha_ingreso",
        header: "F. Ing.",
        value: (a) => a.fecha_ingreso ?? "-",
        pdfCellWidth: 16,
      },
      {
        id: "status",
        header: "Estado",
        value: (a) => a.status_label ?? "-",
        pdfCellWidth: 16,
      },
      {
        id: "quantity",
        header: "Cant.",
        value: (a) => Number(a.stock_total ?? 0),
        pdfCellWidth: 10,
      },
      {
        id: "amount_per_container",
        header: "Cant. x Cont.",
        value: (a) => getAmountPerContainerSummaryLabel(a),
        pdfCellWidth: 14,
      },
      {
        id: "saldo",
        header: "Saldo",
        value: (a) => Number(a.saldo ?? a.total ?? 0),
        pdfCellWidth: 12,
      },
      {
        id: "unit",
        header: "UM",
        value: (a) => a.unit_description ?? "-",
        pdfCellWidth: 13,
      },
      {
        id: "presentation",
        header: "Presentación",
        value: (a) => a.presentation ?? "-",
        pdfCellWidth: 18,
      },
      {
        id: "observation",
        header: "Observación",
        value: (a) => a.observation ?? "-",
        pdfCellWidth: 28,
      },
    );

    return columns;
  };

  const getStockSummaryExportSubtitle = () =>
    queryClientIds.length === 0
      ? "Clientes: " +
        (userData?.role === Roles.CLIENT
          ? `${userData?.client?.code} - ${userData?.client?.razon_social}`
          : "TODOS")
      : `Clientes: ${clients
          .filter((c) => queryClientIds.includes(c.id))
          .map((c) => `${c.code} - ${c.razon_social}`)
          .join(", ")}`;

  const handleExportRequest = (format: "excel" | "pdf") => {
    if (exportLoading) return;
    if (isClientUser) {
      void handleExport("none", format);
      return;
    }
    setPendingExportFormat(format);
    setExportModalOpen(true);
  };

  const closeExportModal = () => {
    if (exportLoading) return;
    setExportModalOpen(false);
    setPendingExportFormat(null);
  };

  const handleExport = async (
    groupBy: StockExportGroupBy,
    format: "excel" | "pdf" | null = pendingExportFormat,
  ) => {
    if (!format || exportLoading) return;

    const { headerRows } = buildExcelHeader(clients, filters, userData);

    try {
      setExportLoading(true);
      const response = await axiosGet(
        `${ARTICLE_LOCATIONS_SUMMARY_EXPORT_URL}${buildStockSummaryExportQuery(
          groupBy,
        )}`,
        true,
      );

      if (response.status !== STATUS_CODES.OK) {
        throw new Error(
          response.data?.message ?? "No se pudo obtener el stock para exportar.",
        );
      }

      const exportData = response.data as ArticleLocationSummaryExportResponse;
      const columns = buildStockSummaryExportColumns(groupBy);

      if (format === "excel") {
        if (groupBy === "none") {
          exportStockToExcel({
            rows: exportData.rows ?? [],
            columns,
            sheetName: "Stock Resumido",
            fileNamePrefix: "StockResumido",
            headerRows,
          });
        } else {
          exportGroupedStockToExcel({
            sections: exportData.sections ?? [],
            columns,
            groupBy,
            sheetName: "Stock Resumido",
            fileNamePrefix: "StockResumido",
            headerRows,
          });
        }
      } else {
        if (groupBy === "none") {
          exportStockToPdf({
            rows: exportData.rows ?? [],
            columns,
            title: "Stock Resumido",
            subtitle: getStockSummaryExportSubtitle(),
            fileNamePrefix: "StockResumido",
            tableFontSize: 6.5,
            tableCellPadding: 1,
          });
        } else {
          exportGroupedStockToPdf({
            sections: exportData.sections ?? [],
            columns,
            groupBy,
            title: "Stock Resumido",
            subtitle: getStockSummaryExportSubtitle(),
            fileNamePrefix: "StockResumido",
            tableFontSize: 6.5,
            tableCellPadding: 1,
          });
        }
      }

      setExportModalOpen(false);
      setPendingExportFormat(null);
    } catch (error) {
      console.error(error);
      showSnackBar(
        error instanceof Error
          ? error.message
          : "No se pudo preparar el stock para exportar.",
        "error",
      );
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Typography variant="h6">Filtros</Typography>

          <Grid container spacing={2} my={1}>
            {userData?.role !== Roles.CLIENT && (
              <Grid item md={3} sm={4} xs={12}>
                <Autocomplete
                  multiple
                  options={clients.filter((c) => c.parent_client_id == null).map((c) => ({
                    ...c,
                    label: `${c.code} - ${c.razon_social}`,
                  }))}
                  onChange={(_, value) => {
                    setSelectedClients(value);
                    setSelectedLineClients([]);
                    setFilters((prev) => ({
                      ...prev,
                      clientIds: value.map((v) => v.id),
                      lineIds: [],
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Clientes" />
                  )}
                  disabled={stockSummaryLoading}
                />
              </Grid>
            )}

            {(!isClientUser || lineOptions.length > 0) && (
              <Grid item md={3} sm={4} xs={12}>
                <Autocomplete
                  multiple
                  options={lineOptions}
                  value={selectedLineClients}
                  getOptionLabel={(option) => `${option.code} - ${option.razon_social}`}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  onChange={(_, value) => {
                    setSelectedLineClients(value);
                    setFilters((prev) => ({
                      ...prev,
                      lineIds: value.map((v) => v.id),
                    }));
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label="Líneas" />
                  )}
                  disabled={
                    (!isClientUser && filters.clientIds.length === 0) ||
                    lineOptions.length === 0 ||
                    stockSummaryLoading
                  }
                />
              </Grid>
            )}

            <Grid item md={3} sm={6} xs={12}>
              <Autocomplete
                options={groupOptions}
                getOptionLabel={(option) => option.description ?? ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) =>
                  options.filter((o) =>
                    o.description
                      ?.toLowerCase()
                      .includes(state.inputValue.toLowerCase()),
                  )
                }
                onChange={(_, value) =>
                  setFilters((prev) => ({ ...prev, groupId: value?.id }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Grupo de artículos" />
                )}
                disabled={stockSummaryLoading}
              />
            </Grid>

            <Grid item md={3} sm={4} xs={12}>
              <Autocomplete
                disableClearable
                options={stockStatusOptions}
                value={getStockStatusOption(filters.status)}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    status: value?.id ?? "all",
                  }))
                }
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => (
                  <TextField {...params} label="Estado de mercadería" />
                )}
                disabled={stockSummaryLoading}
              />
            </Grid>

            <Grid item md={3} sm={4} xs={12}>
              <Autocomplete
                options={uniqueArticles}
                value={
                  uniqueArticles.find(
                    (article) => article.code === filters.articleCode,
                  ) ?? null
                }
                getOptionLabel={(option) => option.code ?? ""}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                filterOptions={(options, state) =>
                  options.filter((o) =>
                    o.code
                      ?.toLowerCase()
                      .includes(state.inputValue.toLowerCase()),
                  )
                }
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    articleCode: value?.code ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Artículo" />
                )}
                disabled={stockSummaryLoading}
              />
            </Grid>

            <Grid item md={3} sm={4} xs={12}>
              <Autocomplete
                options={uniqueAmountPerContainer.map((v) => ({
                  label: String(v),
                }))}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    amount_per_container: value?.label ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Cant. x Contenedor" />
                )}
                disabled={stockSummaryLoading}
              />
            </Grid>

            {filterVisibility.show_lote && (
              <Grid item md={3} sm={4} xs={12}>
                <Autocomplete
                  options={uniqueLotes.map((v) => ({ label: v }))}
                  onChange={(_, value) =>
                    setFilters((prev) => ({
                      ...prev,
                      lote: value?.label ?? "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Lote" />
                  )}
                  disabled={stockSummaryLoading}
                />
              </Grid>
            )}

            {filterVisibility.show_oc && (
              <Grid item md={3} sm={4} xs={12}>
                <Autocomplete
                  options={uniqueOCs.map((v) => ({ label: v }))}
                  onChange={(_, value) =>
                    setFilters((prev) => ({ ...prev, oc: value?.label ?? "" }))
                  }
                  renderInput={(params) => <TextField {...params} label="OC" />}
                  disabled={stockSummaryLoading}
                />
              </Grid>
            )}

            {filterVisibility.show_fecha_vencimiento && (
              <Grid item md={3} sm={4} xs={12}>
                <Autocomplete
                  options={uniqueVencimientos.map((v) => ({ label: v }))}
                  onChange={(_, value) =>
                    setFilters((prev) => ({
                      ...prev,
                      fecha_vencimiento: value?.label ?? "",
                    }))
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Fecha Vencimiento" />
                  )}
                  disabled={stockSummaryLoading}
                />
              </Grid>
            )}

            <Grid item md={3} sm={4} xs={12}>
              <TextField
                label="Numero de Ingreso"
                type="text"
                fullWidth
                value={movementIdInput}
                onChange={(e) => setMovementIdInput(e.target.value)}
                onBlur={() =>
                  setFilters((prev) => ({
                    ...prev,
                    movementId: movementIdInput.trim(),
                  }))
                }
                disabled={stockSummaryLoading}
              />
            </Grid>

            <Grid item md={3} sm={4} xs={12}>
              <Autocomplete
                options={uniqueIngresos.map((v) => ({ label: v }))}
                onChange={(_, value) =>
                  setFilters((prev) => ({
                    ...prev,
                    fecha_ingreso: value?.label ?? "",
                  }))
                }
                renderInput={(params) => (
                  <TextField {...params} label="Fecha Ingreso" />
                )}
                disabled={stockSummaryLoading}
              />
            </Grid>

            <Grid item md={3} sm={6} xs={12}>
              <TextField
                label="Descripción de Artículo"
                type="text"
                fullWidth
                value={articleDescriptionInput}
                onChange={(e) => setArticleDescriptionInput(e.target.value)}
                onBlur={() =>
                  setFilters((prev) => ({
                    ...prev,
                    articleDescription: articleDescriptionInput.trim(),
                  }))
                }
                disabled={stockSummaryLoading}
              />
            </Grid>
          </Grid>

          <Grid
            container
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Stock Resumido</Typography>

            <Grid item>
              <Grid container spacing={2}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="success"
                    startIcon={<FaFileDownload />}
                    onClick={() => handleExportRequest("excel")}
                    disabled={stockSummary.length === 0 || exportLoading}
                  >
                    Exportar Excel
                  </Button>
                </Grid>

                <Grid item>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={<FaFileDownload />}
                    onClick={() => handleExportRequest("pdf")}
                    disabled={stockSummary.length === 0 || exportLoading}
                  >
                    Exportar PDF
                  </Button>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          {stockSummaryLoading ? (
            <Grid item xs={12} display="flex" justifyContent="center" my={6}>
              <CircularProgress />
            </Grid>
          ) : (
            <CustomGrid<ArticleLocationSummary>
              structure={structure}
              rows={stockSummary.map((r, index) => ({ ...r, id: index }))}
              selectionModel={selectionModel}
              onRowSelection={setSelectionModel}
              checkboxSelection={false}
            />
          )}

          {!stockSummaryLoading && stockSummary.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                No hay resultados disponibles
              </Typography>
            </Alert>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12}>
        <Paper elevation={4} sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">Pre-Picking</Typography>
              <Typography variant="body2" color="text.secondary">
                Valida stock disponible agrupado por articulo, lote, OC, fecha
                de vencimiento y fecha de ingreso, sin generar movimientos.
              </Typography>
            </Grid>

            {userData?.role !== Roles.CLIENT && (
              <Grid item xs={12} md={6}>
                <FormControl fullWidth margin="none">
                  <Autocomplete
                    id="stock-summary-prepicking-client"
                    autoComplete
                    value={
                      prePickingSelectedClient
                        ? {
                            id: prePickingSelectedClient.id,
                            label: `${prePickingSelectedClient.code} - ${prePickingSelectedClient.razon_social}`,
                          }
                        : null
                    }
                    options={activeClients.map((client) => ({
                      id: client.id,
                      label: `${client.code} - ${client.razon_social}`,
                    }))}
                    isOptionEqualToValue={(option, value) =>
                      option.id === value.id
                    }
                    onChange={(_, value) => {
                      setPrePickingClientId(value?.id ?? null);
                      setPrePickingValidationResult(null);
                    }}
                    renderInput={(params) => (
                      <TextField {...params} required label="Cliente" />
                    )}
                  />
                </FormControl>
              </Grid>
            )}

            {userData?.role === Roles.CLIENT && prePickingSelectedClient && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Cliente: {prePickingSelectedClient.code} -{" "}
                  {prePickingSelectedClient.razon_social}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12}>
              <FileImportPrePicking
                clientId={prePickingClientId}
                disabled={!prePickingClientId}
                articles={prePickingArticles}
                onValidated={handlePrePickingValidated}
              />
            </Grid>

            {!prePickingClientId && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary">
                  Seleccione un cliente para habilitar la importacion del Excel
                  de pre-picking.
                </Typography>
              </Grid>
            )}

            {prePickingValidationResult?.success && (
              <Grid item xs={12}>
                <Alert severity="success">
                  <strong>{prePickingValidationResult.message}</strong>
                  <br />
                  {prePickingValidationResult.detail}
                </Alert>
              </Grid>
            )}

            {prePickingShortageResult && (
              <Grid item xs={12}>
                <PrePickingReport
                  client={prePickingShortageResult.client}
                  summary={prePickingShortageResult.summary}
                  report={prePickingShortageResult.report}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Grid>
      <StockExportGroupingModal
        open={!isClientUser && exportModalOpen}
        loading={exportLoading}
        allowUngrouped
        onClose={closeExportModal}
        onConfirm={handleExport}
      />
      <ObservationRecordsModal
        open={selectedObservationRow !== null}
        readOnly
        title="Observaciones del stock resumido"
        subtitle={
          selectedObservationRow
            ? `${selectedObservationRow.article_code} - ${selectedObservationRow.article_description}`
            : null
        }
        records={selectedObservationRecords}
        onClose={() => setSelectedObservationRow(null)}
      />
      <Loader isFirstLoad={false} isLoading={exportLoading} />
    </Grid>
  );
}
